import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Stack,
  Badge,
  Pagination,
  Form,
  DropdownButton,
  Dropdown,
} from "react-bootstrap";
import { ethers } from "ethers";
import {
  presaleAbi,
  presaleAddress,
  network,
  token,
  adminKey,
} from "./utils/contants.js";
import {
  formatBalance,
  copyToClipboard,
  exportSalesToExcel,
} from "./utils/utils.ts";
import {
  useWeb3ModalAccount,
  useWeb3ModalProvider,
} from "@web3modal/ethers/react";
import { Trans, useTranslation } from "react-i18next";
import { FaRegCopy } from "react-icons/fa";
import Swal from "sweetalert2";
import { Sale } from "./types/Sale.ts";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

function AffiliateDashboard() {
  const { t } = useTranslation();
  const { address, chainId, isConnected } = useWeb3ModalAccount();
  const { walletProvider } = useWeb3ModalProvider();
  const [presaleContract, setPresaleContract] =
    useState<ethers.Contract | null>(null);
  const [affiliate, setAffiliate] = useState<any>(null);
  const [affiliateLoaded, setAffiliateLoaded] = useState(false);
  const [totalSales, setTotalSales] = useState(0);
  const [sales, setSales] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isWithdrawingNative, setIsWithdrawingNative] = useState(false);
  const [isWithdrawingUSDT, setIsWithdrawingUSDT] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  // const pageSize = 20;
  const floorDigits = 10;

  const totalPages = useMemo(() => {
    return Math.ceil(totalSales / pageSize);
  }, [totalSales, pageSize]);

  //allow admin to view
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const affiliateAddress = queryParams.get("address");
  const querySecret = queryParams.get("key");
  const authenticatedAdmin = querySecret === adminKey;

  const isAdminView = useMemo(() => {
    if (authenticatedAdmin && affiliateAddress) {
      return true;
    }
  }, [authenticatedAdmin, affiliateAddress]);

  // Initialize the contract connection
  useEffect(() => {
    const initContract = async () => {
      if (!walletProvider || !address) return;
      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const contract = new ethers.Contract(presaleAddress, presaleAbi, signer);
      setPresaleContract(contract);
    };
    if (chainId !== network.chainId && isConnected) {
      Swal.fire(t("error.error"), t("error.invalid_chainid"), "error");
      return;
    } else {
      initContract();
    }
  }, [walletProvider, address, chainId]);

  // Fetch affiliate details from the contract
  useEffect(() => {
    const fetchAffiliateData = async () => {
      if (!presaleContract || !address) return;
      try {
        let reqAddress: string = address;
        if (isAdminView && affiliateAddress) {
          reqAddress = affiliateAddress;
        }
        // Get referral code from codeMap
        const code = await presaleContract.codeMap(reqAddress);
        // const code = await presaleContract.codeMap(address);
        // If no valid code (empty string), the user isn’t an affiliate
        if (!code || code === "") {
          setAffiliate(null);
          setAffiliateLoaded(true);
          return;
        }
        // Get affiliate details using the referral code
        const affiliateData = await presaleContract.affiliateMap(code);
        // Get the latest native price from Chainlink via the contract
        const latestPrice = await presaleContract.getLatestPrice();
        const totalSales = await presaleContract.getTotalSalesAffiliate(code);
        setTotalSales(Number(totalSales));
        // Convert native profit to USDT value:
        // nativeProfitInUSDT = (latestPrice * nativeProfit) / 1e8
        const nativeProfitInUSDT =
          (Number(latestPrice) * Number(affiliateData.nativeProfit)) / 1e8;
        const totalEarningsUSDT =
          Number(affiliateData.usdtProfit) + nativeProfitInUSDT / 1e12;
        setAffiliate({
          referal_code: code,
          profitPercent: Number(affiliateData.profitPercent),
          usdtUnclaimed:
            Number(affiliateData.usdtProfit) -
            Number(affiliateData.usdtWithdrawn),
          nativeUnclaimed:
            Number(affiliateData.nativeProfit) -
            Number(affiliateData.nativeWithdrawn),
          nativeProfitInUSDT,
          totalEarningsUSDT,
          revoked: affiliateData.revoked,
          tokensSold: affiliateData.tokensSold,
          usdtProfit: affiliateData.usdtProfit,
          nativeProfit: affiliateData.nativeProfit,
        });
        setAffiliateLoaded(true);
        console.log(affiliate);
      } catch (error: any) {
        Swal.fire(t("error.error"), t("error.loading_affiliate"), "error");
        console.error(error);
      }
    };
    fetchAffiliateData();
  }, [
    presaleContract,
    address,
    authenticatedAdmin,
    affiliateAddress,
    isAdminView,
  ]);

  const fetchSales = async (offset, pageSize) => {
    if (!presaleContract || !affiliate || !affiliate.referal_code) {
      return {
        sales: [],
        hasMore: false,
      };
    }
    const salesData = await presaleContract.getAffiliateSales(
      affiliate.referal_code,
      pageSize,
      offset
    );
    let parsedSales: Sale[] = [];
    for (let i = 0; i < salesData.length; i++) {
      parsedSales.push(Sale.fromEthersResponse(salesData[i]));
    }

    return {
      sales: parsedSales,
      hasMore: (offset + 1) * pageSize < totalSales,
    };
  };
  const getSalesCurrentView = async () => {
    //find sales of current page
    //fetch sales of current page no , current page no -1 in ordre to
    if (totalPages === 0) return [];
    let pageToFetch = totalPages - (currentPage - 1);
    let { sales: curSales } = await fetchSales(pageToFetch - 1, pageSize);
    curSales = curSales.slice(0, totalSales % pageSize);
    if (pageToFetch > 1) {
      let { sales: prevSales } = await fetchSales(pageToFetch - 2, pageSize);
      curSales = prevSales.slice(totalSales % pageSize).concat(curSales);
    }
    return curSales.reverse();
  };
  // Fetch referral sales history with pagination
  useEffect(() => {
    const fetchSalesHistory = async () => {
      if (!presaleContract || !affiliate || !affiliate.referal_code) return;
      try {
        //use a shrimmer
        let sales = await getSalesCurrentView();
        setSales(sales);
      } catch (error: any) {
        Swal.fire(t("error.error"), t("error.loading_history"), "error");
        console.error(error);
      }
    };
    fetchSalesHistory();
  }, [presaleContract, affiliate, currentPage, pageSize]);

  // Withdraw native earnings
  const withdrawNative = async () => {
    if (!presaleContract || !isConnected) return;
    setIsWithdrawingNative(true);
    try {
      const tx = await presaleContract.withdrawAffiliateProfitNative();
      await tx.wait();
      Swal.fire(
        t("success.success"),
        t("success.bnb_withdraw", {
          symbol: network.currency,
        }),
        "success"
      );
    } catch (error: any) {
      Swal.fire(t("error.error"), t("error.failed_withdraw"), "error");
    }
    setIsWithdrawingNative(false);
  };

  // Withdraw USDT earnings
  const withdrawUSDT = async () => {
    if (!presaleContract) return;
    setIsWithdrawingUSDT(true);
    try {
      const tx = await presaleContract.withdrawAffiliateProfitUsdt();
      await tx.wait();
      Swal.fire(t("success.success"), t("success.usdt_withdraw"), "success");
    } catch (error: any) {
      Swal.fire(t("error.error"), t("error.failed_withdraw"), "error");
    }
    setIsWithdrawingUSDT(false);
  };

  // Copy referral code and show success message
  const handleCopyReferralCode = async (code: string) => {
    const res = await copyToClipboard(code);
    if (res) {
      Swal.fire(t("user.copied"), t("success.code_copied"), "success");
    } else {
      Swal.fire(t("error.error"), t("error.copy_failed"), "error");
    }
  };

  const performCopy = async (text: string) => {
    const res = await copyToClipboard(text);
    if (res) {
      Swal.fire(t("user.copied"), "", "success");
    } else {
      Swal.fire(t("error.error"), t("error.copy_failed"), "error");
    }
  };

  if (!address)
    return (
      <Container className="text-center mt-5">
        <h3>{t("info.connect_wallet")}</h3>
      </Container>
    );

  if (!affiliateLoaded)
    return (
      <Container className="text-center mt-5">
        <h3>{t("info.fetch_in_progress")}</h3>
      </Container>
    );

  if (!(affiliate || authenticatedAdmin))
    return (
      <Container className="text-center mt-5">
        <h3>{t("info.not_affiliate")}</h3>
      </Container>
    );

  const intiateExport = async () => {
    try {
      let salesHistory: Sale[] = [];

      let offset = 0;
      let pageSize = 20;
      while (true) {
        let { sales, hasMore } = await fetchSales(offset, pageSize);
        salesHistory = salesHistory.concat(sales);
        if (!hasMore) break;
        offset++;
      }

      salesHistory = salesHistory.reverse();
      exportSalesToExcel(salesHistory);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div>
      <div
        style={{
          background: "#3B82F6",
          padding: "12px",
          display: authenticatedAdmin ? "block" : "none",
        }}
      >
        <div
          style={{
            color: "white",
            textAlign: "center",
            fontSize: "18px",
            position: "sticky",
          }}
        >
          You are viewing as Admin
        </div>
      </div>
      <Container className="mt-4">
        {/* Header: Responsive layout for mobile view */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="mb-4">
              <Col xs={12} md={6}>
                <h5>{isAdminView ? affiliateAddress : address}</h5>
                <div>
                  <Badge bg="secondary" className="p-2">
                    {affiliate.referal_code}
                  </Badge>{" "}
                  &nbsp;
                  <FaRegCopy
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      handleCopyReferralCode(affiliate.referal_code)
                    }
                  />
                </div>
              </Col>
              <Col xs={12} md={6} className="text-md-end mt-3 mt-md-0">
                <h5>
                  {t("user.profit_percentage")}: {affiliate?.profitPercent}%
                </h5>
                <Stack
                  direction="horizontal"
                  gap={2}
                  className="justify-content-md-end"
                >
                  {affiliate && affiliate.revoked ? (
                    <Button variant="danger" size="sm" disabled={true}>
                      {t("user.revoked")}
                    </Button>
                  ) : (
                    <Button variant="success" size="sm" disabled={true}>
                      {t("user.active")}
                    </Button>
                  )}
                </Stack>
              </Col>
            </Row>
            {/* Statistics Cards */}
            <Row className="g-4 mb-4">
              <Col md={3} className="text-center">
                <StatCard
                  style={{
                    borderWidth: "2px",
                  }}
                  title={
                    <Trans
                      i18nKey={"user.total_earnings"}
                      components={{
                        i: <span />,
                      }}
                    />
                  }
                  value={`$${formatBalance(
                    affiliate ? affiliate.totalEarningsUSDT : 0,
                    6
                  )}`}
                />
              </Col>
              <Col md={3} className="text-center">
                <StatCard
                  style={{
                    borderWidth: "2px",
                  }}
                  title={t("user.total_tokens_sold")}
                  value={`${formatBalance(
                    Number(affiliate?.tokensSold || 0),
                    token.decimals
                  )} ${token.symbol}`}
                  variant="secondary"
                />
              </Col>
              <Col md={3} className="text-center">
                <StatCard
                  title={
                    <Trans
                      i18nKey="user.usdt_unclaimed"
                      components={{ i: <i className="text-warning" /> }}
                    />
                  }
                  value={`$${formatBalance(
                    Number(affiliate?.usdtUnclaimed || 0),
                    6
                  )}`}
                  variant="warning"
                />
              </Col>
              <Col md={3} className="text-center">
                <StatCard
                  title={
                    <Trans
                      i18nKey="user.bnb_unclaimed"
                      components={{
                        i: <i className="text-danger" />,
                      }}
                      values={{
                        symbol: network.currency,
                      }}
                    />
                  }
                  value={`${formatBalance(
                    Number(affiliate?.nativeUnclaimed || 0),
                    18
                  ).substring(0, floorDigits)} ${network.currency}`}
                  variant="danger"
                />
              </Col>
            </Row>

            {/* New Profit & Withdraw Section */}
            <Row className="g-4 mb-4 text-center">
              {/* Right side: Withdraw Sections */}

              {/* Left side: Total Profit Cards */}
              <Col md={3}>
                <Card border="primary" className="mb-4">
                  <Card.Body>
                    <h5 className="mb-3">
                      {
                        <Trans
                          i18nKey="user.total_usdt_profit"
                          components={{ i: <i className="text-warning" /> }}
                        />
                      }
                    </h5>
                    <h4 className="text-warning">
                      $ {formatBalance(Number(affiliate?.usdtProfit || 0), 6)}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card border="secondary">
                  <Card.Body>
                    <h5 className="mb-3">
                      <Trans
                        i18nKey="user.total_bnb_profit"
                        components={{ i: <i className="text-danger" /> }}
                        values={{
                          symbol: network.currency,
                        }}
                      />
                    </h5>
                    <h4 className="text-danger">
                      {formatBalance(
                        Number(affiliate?.nativeProfit || 0),
                        18
                      ).substring(0, floorDigits)}{" "}
                      {network.currency}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="mb-4" border="warning">
                  <Card.Body>
                    <h5 className="mb-3">
                      {
                        <Trans
                          i18nKey="user.withdraw_usdt"
                          components={{ i: <i className="text-warning" /> }}
                        />
                      }
                    </h5>
                    <Button
                      variant="warning"
                      onClick={withdrawUSDT}
                      disabled={
                        !affiliate?.usdtUnclaimed ||
                        Number(affiliate.usdtUnclaimed) === 0 ||
                        isWithdrawingUSDT
                      }
                    >
                      {isWithdrawingUSDT ? (
                        <Trans
                          i18nKey="user.withdrawing_usdt"
                          components={{ i: <i /> }}
                        />
                      ) : (
                        <div>
                          <Trans
                            i18nKey="user.withdraw_usdt"
                            components={{ i: <i /> }}
                          />
                          &nbsp; ($
                          {formatBalance(
                            Number(affiliate?.usdtUnclaimed || 0),
                            6
                          )}
                          )
                        </div>
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card border="danger">
                  <Card.Body>
                    <h5 className="mb-3">
                      <Trans
                        i18nKey="user.withdraw_bnb"
                        components={{ i: <i className="text-danger" /> }}
                        values={{
                          symbol: network.currency,
                        }}
                      />
                    </h5>
                    <Button
                      variant="danger"
                      onClick={withdrawNative}
                      disabled={
                        !affiliate?.nativeUnclaimed ||
                        Number(affiliate.nativeUnclaimed) === 0 ||
                        isWithdrawingNative
                      }
                    >
                      {isWithdrawingNative ? (
                        <Trans
                          i18nKey="user.withdrawing_bnb"
                          components={{ i: <i /> }}
                          values={{
                            symbol: network.currency,
                          }}
                        />
                      ) : (
                        <div>
                          <Trans
                            i18nKey="user.withdraw_bnb"
                            components={{ i: <i /> }}
                            values={{
                              symbol: network.currency,
                            }}
                          />
                          &nbsp; &nbsp; (
                          {formatBalance(
                            Number(affiliate?.nativeUnclaimed || 0),
                            18
                          ).substring(0, floorDigits)}{" "}
                          {network.currency})
                        </div>
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Referral History with Search */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="g-4 mb-3">
              <Col md={7}>
                <h5>{t("user.referral_history")}</h5>
              </Col>
              <div style={{ flex: 1 }}></div>
              <Col md={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("user.show")}
                  <DropdownButton
                    id="dropdown-pages"
                    title={pageSize}
                    onSelect={(e) => {
                      setPageSize(Number(e));
                    }}
                  >
                    {[10, 20, 30, 40, 50].map((num) => (
                      <Dropdown.Item eventKey={num} key={num}>
                        {num}
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>
                  {t("user.per_page")}
                </div>
              </Col>
              <Col md={1}>
                <div className="">
                  <Button
                    onClick={() => {
                      intiateExport();
                    }}
                    variant="primary"
                  >
                    {t("user.export")}
                  </Button>
                </div>
              </Col>
            </Row>
            {sales.length === 0 ? (
              <div className="text-center py-4">
                <h5>{t("info.no_referral_found")}</h5>
              </div>
            ) : (
              <>
                <div
                  style={{
                    maxWidth: isMobile ? "100%" : "calc(100vw - 364px)",
                  }}
                >
                  <Table
                    striped
                    bordered
                    hover
                    responsive
                    className="text-center"
                  >
                    <thead className="text-nowrap">
                      <tr>
                        <th>S.No</th>
                        <th>{t("user.user_address")}</th>
                        <th>
                          {t("user.invest_eth", { symbol: network.currency })}
                        </th>
                        <th>{t("user.invest_usdt")}</th>
                        <th>{t("user.is_usdt_purchase")}</th>
                        <th>{t("user.tokens_received")}</th>
                        <th>
                          {t("user.profit_eth", { symbol: network.currency })}
                        </th>
                        <th>{t("user.profit_usdt")}</th>
                        <th>{t("user.date")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales.map((sale, index) => (
                        <tr key={index} className="text-nowrap">
                          <td>
                            {totalSales -
                              ((currentPage - 1) * pageSize + index)}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {sale.user}
                            <img
                              src="/copy-file-icon.svg"
                              height={24}
                              width={24}
                              className="ms-3"
                              style={{ cursor: "pointer" }}
                              alt="copy"
                              onClick={() => performCopy(sale.user)}
                            />
                          </td>
                          <td>
                            {sale.isUsdtPurchase
                              ? `0`
                              : `${formatBalance(Number(sale.investment), 18)}`}
                          </td>
                          <td>
                            {sale.isUsdtPurchase
                              ? `${formatBalance(Number(sale.investment), 6)}`
                              : `0`}
                          </td>
                          <td>{sale.isUsdtPurchase ? "Yes" : "No"}</td>
                          <td>
                            {formatBalance(
                              Number(sale.tokensTransferred),
                              token.decimals
                            )}{" "}
                          </td>
                          <td>
                            {sale.isUsdtPurchase
                              ? `0`
                              : `${formatBalance(Number(sale.profit), 18)}`}
                          </td>
                          <td>
                            {sale.isUsdtPurchase
                              ? `${formatBalance(Number(sale.profit), 6)}`
                              : `0`}
                          </td>
                          <td>{sale.purchaseTime.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
            <Pagination className="justify-content-center">
              <Pagination.First
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() =>
                  setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev))
                }
                disabled={currentPage === 1}
              />

              {currentPage > 2 && (
                <>
                  <Pagination.Item onClick={() => setCurrentPage(1)}>
                    1
                  </Pagination.Item>
                  {currentPage > 3 && <Pagination.Ellipsis disabled />}
                </>
              )}

              {currentPage > 1 && (
                <Pagination.Item
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  {currentPage - 1}
                </Pagination.Item>
              )}

              <Pagination.Item active>{currentPage}</Pagination.Item>

              {currentPage < totalPages && (
                <Pagination.Item
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  {currentPage + 1}
                </Pagination.Item>
              )}

              {currentPage < totalPages - 1 && (
                <>
                  {currentPage < totalPages - 2 && (
                    <Pagination.Ellipsis disabled />
                  )}
                  <Pagination.Item onClick={() => setCurrentPage(totalPages)}>
                    {totalPages}
                  </Pagination.Item>
                </>
              )}

              <Pagination.Next
                onClick={() =>
                  setCurrentPage((prev) =>
                    currentPage < totalPages ? prev + 1 : prev
                  )
                }
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}
const StatCard = ({ title, value, variant = "primary", style }: any) => (
  <Card className={`border-${variant} shadow`} style={style}>
    <Card.Body>
      <h5 className="mb-2">{title}</h5>
      <h4 className={`text-${variant}`}>{value}</h4>
    </Card.Body>
  </Card>
);

export default AffiliateDashboard;
